import { gql } from "@apollo/client";
import Image from "next/image";
import MigelConformity from "../../../shop/components/MigelConformity";
import ProductAvailability from "../../../shop/components/ProductAvailability";
import getAvailability from "../../../shop/utils/getAvailability";
import { useTranslations } from "next-intl";
import Icon from "../../../common/components/Icon";
import CuttingInfo from "./CuttingInfo";
import { Link } from "../../../../navigation";

export const ProductItemFragment = gql`
  fragment ProductItemFragment on SimpleProduct {
    _id
    status
    texts {
      _id
      title
      slug
      description
    }
    media {
      _id
      file {
        _id
        url(version: "MEDIUM")
      }
    }
    simulatedStocks {
      quantity
    }
    simulatedDispatches {
      earliestDelivery
    }
    sku
    publicareTranslatedSalesUnit
    salesQuantityPerUnit
    publicare {
      clientMigelConformity
      institutionalMigelConformity
      migel
      cutting
    }
  }
`;

const ProductItem = ({
  texts,
  disabled,
  sku,
  publicareTranslatedSalesUnit,
  salesQuantityPerUnit,
  media,
  children,
  simulatedStocks,
  simulatedDispatches,
  publicare,
  shouldUseInstitutionalMigelConformity,
  hideAvailability = false,
  cutting,
  disableNavigation = false,
}) => {
  const migelConformity = shouldUseInstitutionalMigelConformity
    ? publicare?.institutionalMigelConformity
    : publicare?.clientMigelConformity;

  const t = useTranslations();
  const firstMediaUrl = media?.[0]?.file?.url;

  const { availability, earliestDeliveryDate } = getAvailability({
    stocks: simulatedStocks,
    dispatches: simulatedDispatches,
  });

  return (
    <div className="product-item py-3 position-relative">
      <div
        className={`product-item__info ${disabled ? "color-primary-light" : ""}`}
        aria-disabled={disabled}
      >
        {firstMediaUrl && (
          <div className="g-image">
            <Image
              src={firstMediaUrl}
              unoptimized // Disable Next.js loader because it's already a thumbnail from thumbor
              alt=""
              width={100}
              height={100}
              priority={false}
              style={{ objectFit: "contain", objectPosition: "center" }}
            />
          </div>
        )}

        <div className="g-title fw-semibold">
          <div className="d-flex gap-2 align-items-center justify-content-between w-100">
            <span className="product-item__title">{texts?.title}</span>
            {!disableNavigation && (
              <Link
                className="product-item__link flex-shrink-0"
                prefetch={false}
                href={texts?.slug ? `/product/${texts?.slug}` : ""}
              >
                <Icon icon="visibility-on" className="icon--l" />
              </Link>
            )}
          </div>
          {publicare?.cutting ? <CuttingInfo cutting={cutting} /> : null}
        </div>
        <div className="g-data d-flex flex-column">
          <div className="h-20px color-primary-dimmed d-flex align-items-center justify-content-between gap-2">
            <span className="fs-data">{t("article_number_label")}</span>
            <span className="fs-data">{sku}</span>
          </div>
          {salesQuantityPerUnit && (
            <div className="h-20px color-primary-dimmed d-flex align-items-center justify-content-between gap-2">
              <span className="fs-data">{t("article_quantity_per_unit")}</span>
              <span className="fs-data">
                {publicareTranslatedSalesUnit} à {salesQuantityPerUnit}
              </span>
            </div>
          )}
          {!hideAvailability && (
            <div className="h-20px d-flex align-items-center justify-content-between gap-2">
              <small className="color-primary-dimmed fs-8">
                {t("availability")}
              </small>
              <ProductAvailability
                className="icon--mdl"
                availability={availability}
                earliestDeliveryDate={earliestDeliveryDate}
              />
            </div>
          )}

          {migelConformity && (
            <div className="h-20px d-flex align-items-center justify-content-between gap-2">
              <small className="color-primary-dimmed fs-8">
                {t("migel-conformity")}
              </small>
              <MigelConformity migelConformity={migelConformity} />
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
};

export default ProductItem;
